import { INTEREST_KEYWORDS } from "@/api/constants/leads";
import { djs } from "@/lib/djs";

const KNOWN_DDI = ["549", "351", "54", "44", "49", "39", "34", "33", "1"];

const MAX_AGE = 110;
const MIN_AGE = 13;

const MIN_REPORTED_AGE = 1;
const MAX_REPORTED_AGE = 120;

const ISO_PREFIX = /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}(:\d{2})?)?$/u;

const NON_PATIENT =
	/amig[oa]|irm[ãa]o?|marido|esposa|m[ãa]e d[ao]|consultor|fornec|s[ãa]o rafael/iu;

const B2B_SENDPULSE =
	/financ|fornec|comercial|contabil|laborat|consultorio|recep[cs]|administra|marketing|gerenciamento|ltda|sao rafael|new\s?form|star tech|residuos|digitalizy|metta|clinica/iu;

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/u;
const EMAIL_TLD = /\.(com|net|org|br|me|io|info|edu|gov)$/u;

const REGISTRATION_FORMAT = {
	erp: "DD/MM/YYYY HH:mm",
	sendpulse: "DD/MM/YYYY HH:mm:ss",
};

const MONDAY_UPDATE = /^(?<op>.*?)\s*(?<stamp>[A-Z][a-z]{2} \d{1,2}, \d{4} \d{1,2}:\d{2} [AP]M)$/u;
const MONDAY_ADDRESS =
	/^(?<street>[^-]+?)\s*-\s*(?<district>[^,]+?),\s*(?<city>.+?)\s*-\s*(?<state>[A-Z]{2}),\s*Brasil$/u;
const NUMBER_COMPLEMENT = /^(?<number>\d+)[\s,]*(?<rest>.*)$/u;

type RegistrationOrigin = keyof typeof REGISTRATION_FORMAT;

function digits(raw: string | number) {
	let s = String(raw).trim();

	if (s.endsWith(".0")) {
		s = s.slice(0, -2);
	}

	return { s, d: s.replaceAll(/\D/gu, "") };
}

function checkDigits(cpf: string) {
	if (new Set(cpf).size === 1) {
		return false;
	}

	for (const pos of [9, 10]) {
		let sum = 0;

		for (let i = 0; i < pos; i++) {
			sum += Number(cpf[i]) * (pos + 1 - i);
		}

		if (((sum * 10) % 11) % 10 !== Number(cpf[pos])) {
			return false;
		}
	}

	return true;
}

export const Normalize = {
	fold(raw: string) {
		return raw
			.normalize("NFKD")
			.replaceAll(/\p{Diacritic}/gu, "")
			.toLowerCase()
			.replaceAll(/\s+/gu, " ")
			.trim();
	},

	slug(raw: string) {
		return raw
			.normalize("NFD")
			.replaceAll(/\p{Diacritic}/gu, "")
			.toLowerCase()
			.replaceAll(/[^a-z0-9]+/gu, "-")
			.replaceAll(/^-+|-+$/gu, "");
	},

	phone(raw: string | number | null | undefined) {
		if (raw === null || raw === undefined) {
			return null;
		}

		const { s, d } = digits(raw);

		if (!d || (new Set(d).size === 1 && d[0] === "0")) {
			return null;
		}

		const res: {
			raw: string;
			norm: string | null;
			kind: "mobile" | "landline" | "international" | "unknown";
			country_code: string | null;
		} = { raw: s, norm: null, kind: "unknown", country_code: null };

		let local: string;

		if (d.startsWith("55") && d.length >= 12 && d.length <= 13) {
			local = d.slice(2);
			res.country_code = "55";
		} else if (d.length >= 10 && d.length <= 11) {
			local = d;
			res.country_code = "55";
		} else {
			for (const ddi of KNOWN_DDI) {
				if (d.startsWith(ddi) && d.length >= 11) {
					return { ...res, kind: "international" as const, norm: d, country_code: ddi };
				}
			}

			return res;
		}

		const third = local.length >= 3 ? local[2] : "";

		if (local.length === 11 && third === "9") {
			return { ...res, kind: "mobile" as const, norm: local };
		}

		if (local.length === 10 && "2345".includes(third)) {
			return { ...res, kind: "landline" as const, norm: local };
		}

		if (local.length === 10 && "6789".includes(third)) {
			return { ...res, kind: "mobile" as const, norm: `${local.slice(0, 2)}9${local.slice(2)}` };
		}

		return res;
	},

	cpf(raw: string | number | null | undefined) {
		if (raw === null || raw === undefined) {
			return null;
		}

		let { d } = digits(raw);

		if (!d) {
			return null;
		}

		if (d.length === 10) {
			d = d.padStart(11, "0");
		}

		if (d.length !== 11) {
			return { norm: d, valid: false };
		}

		return { norm: d, valid: checkDigits(d) };
	},

	birth(raw: string | null | undefined, origin: "br" | "iso") {
		if (raw === null || raw === undefined) {
			return null;
		}

		const s = raw.trim();

		const parsed =
			origin === "iso"
				? ISO_PREFIX.test(s)
					? djs(s.split(" ")[0], "YYYY-MM-DD", true)
					: null
				: djs(s, "DD/MM/YYYY", true);

		if (!parsed || !parsed.isValid()) {
			return { date: null, suspect: true };
		}

		const today = djs();
		const age = today.diff(parsed, "day") / 365.25;
		const placeholder = parsed.format("YYYY-MM-DD") === "1900-01-01";

		if (parsed.isAfter(today, "day") || age < MIN_AGE || age > MAX_AGE || placeholder) {
			return { date: null, suspect: true };
		}

		return { date: parsed.toDate(), suspect: false };
	},

	day(date: Date | null | undefined) {
		if (date === null || date === undefined) {
			return null;
		}

		return djs(date).format("YYYY-MM-DD");
	},

	birthFromAge(age: number | undefined) {
		if (age === undefined) {
			return null;
		}

		if (!Number.isInteger(age) || age < MIN_REPORTED_AGE || age > MAX_REPORTED_AGE) {
			return null;
		}

		return { date: djs().startOf("year").subtract(age, "year").toDate(), suspect: true as const };
	},

	registrationTimestamp(raw: string | null, origin: RegistrationOrigin) {
		if (raw === null) {
			return null;
		}

		const parsed = djs(raw.trim(), REGISTRATION_FORMAT[origin], true);

		if (!parsed.isValid()) {
			return null;
		}

		return parsed.format("YYYY-MM-DDTHH:mm:ss");
	},

	name(raw: string | null) {
		if (raw === null) {
			return null;
		}

		const name = raw.trim();

		if (!name) {
			return null;
		}

		const note =
			[...name.matchAll(/\(([^)]*)\)?/gu)]
				.map((m) => m[1])
				.join(" ")
				.trim() || null;
		const base = name.replaceAll(/\([^)]*\)?/gu, "");
		const nameNorm = this.fold(base);
		const target = `${name} ${note ?? ""}`;
		const suspectedNonPatient = NON_PATIENT.test(target) || nameNorm.includes("rafaela nicholls");

		return {
			name,
			name_norm: nameNorm,
			note,
			suspected_non_patient: suspectedNonPatient,
		};
	},

	classifySendpulse(raw: string | null) {
		if (raw === null) {
			return "patient_potential" as const;
		}

		const folded = this.fold(raw);

		if (B2B_SENDPULSE.test(folded)) {
			return "non_patient" as const;
		}

		return "patient_potential" as const;
	},

	categorizeInterest(name: string) {
		const folded = this.fold(name);

		const match = INTEREST_KEYWORDS.find((entry) =>
			entry.keywords.some((keyword) => folded.includes(keyword)),
		);

		if (match === undefined) {
			return null;
		}

		return match.category;
	},

	cep(raw: string | number | null | undefined) {
		if (raw === null || raw === undefined) {
			return null;
		}

		const { s, d } = digits(raw);

		if (!d) {
			return null;
		}

		if (s.includes("_") || d.length > 8 || d.length < 7) {
			return { cep: null, invalid: true };
		}

		return { cep: d.padStart(8, "0"), invalid: false };
	},

	email(raw: string | null) {
		if (raw === null) {
			return null;
		}

		const email = raw.trim().toLowerCase();

		if (!email) {
			return null;
		}

		const valid = EMAIL_SHAPE.test(email) && EMAIL_TLD.test(email);

		return { email, invalid: !valid };
	},

	mondayUpdate(raw: string | null) {
		if (raw === null) {
			return null;
		}

		const match = MONDAY_UPDATE.exec(raw.trim());

		if (!match || !match.groups) {
			return { operator: null, updated_at: null };
		}

		const parsed = djs(match.groups.stamp, "MMM D, YYYY h:mm A", true);

		if (!parsed.isValid()) {
			return { operator: null, updated_at: null };
		}

		return {
			operator: match.groups.op || null,
			updated_at: parsed.format("YYYY-MM-DDTHH:mm:ss"),
		};
	},

	mondayAddress(text: string | null, numberComplement: string | null) {
		const parts = [text, numberComplement]
			.map((value) => value?.trim())
			.filter((value): value is string => !!value);

		if (parts.length === 0) {
			return null;
		}

		const raw = parts.join(" | ");
		const match = text ? MONDAY_ADDRESS.exec(text.trim()) : null;

		if (!match || !match.groups || match.groups.district.includes(" - ")) {
			return { raw, status: "unparsed" as const };
		}

		const detail = this.numberComplement(numberComplement);

		return {
			raw,
			status: "parsed" as const,
			street: match.groups.street.trim(),
			district: match.groups.district.trim(),
			city: match.groups.city.trim(),
			state: match.groups.state,
			number: detail.number,
			complement: detail.complement,
		};
	},

	numberComplement(raw: string | null) {
		const value = raw?.trim();

		if (!value) {
			return { number: null, complement: null };
		}

		const match = NUMBER_COMPLEMENT.exec(value);

		if (!match || !match.groups) {
			return { number: null, complement: value };
		}

		return { number: match.groups.number, complement: match.groups.rest.trim() || null };
	},
};
